import Vue from 'vue';
import {Component, Mixins, Watch} from "vue-property-decorator";
import {
    bindUser,
    getToken,
    ignoreRoutes, resubscribeToWebPush,
    setRouterEvents,
    subscribeToTokenUpdate,
} from '@/services/auth-service';
import {IAuthData} from "@/model/IAuthData";
import {initMicroFronts} from "@/services/micro-front-service";
import {subscribeToNotification} from "@/services/notification-service";
import {connect} from "@/services/stomp-service";
import {initMap} from "@/services/map-service";
import UserInfo from "@/components/UserInfo.vue";
import NotifyPanel from "@/components/NotifyPanel.vue";
import {IMenuEntity} from "@/model/menu/IMenuEntity";
import {IMenuSubEntity} from "@/model/menu/IMenuSubEntity";
import {IItemsElement} from "@/model/menu/IItemsElement";
import SecurityMixin from "water-rest/src/mixins/SecurityMixin.vue";
import {IHeaderInfo} from "water-rest/src/model/sharedStore/IHeaderInfo";
import HeaderActions from "@/components/HeaderActions.vue";
import LayoutObserver from "@/components/LayoutObserver.vue";
import {setGlobalRir, setGlobalThemeType, ThemeType, toggleHtmlTheme} from "@/services/theme-service";
import {mapActions, mapGetters} from "vuex";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import {ThemeConfig} from "@/model/config/ThemeConfig";
import { ModulePresetModel } from 'water-rest/src/model/messaging/ModulePresetModel';
import { getModulePresets } from 'water-rest/src/service/MessagingService';
import { errorToast, successToast, progressToast } from 'water-layout/src/services/toastr-service';
import { Fetch } from "water-rest/src/fetch/Fetch";
import NotificationHandler from '@/components/notifications/NotificationHandler.vue';
import { NotificationDto } from 'water-rest/src/model/messaging/NotificationDto';
import { onMessageReceive } from "@/services/notification-service";
import { Route } from 'vue-router';
import ConfirmModal from 'water-layout/src/components/modals/ConfirmModal.vue';
import { setRirNotificationInistance } from 'water-layout/src/services/toastr-service';
import { clearPaginationSettings } from 'water-layout/src/services/pagination-service';

@Component({
    components: {
        UserInfo,
        NotifyPanel,
        HeaderActions,
        LayoutObserver,
        Breadcrumbs,
        NotificationHandler,
    },
    computed: {...mapGetters('themeModule', ['themeConfig', 'isThemeConfigLoading'])},
    methods: {...mapActions('themeModule', ['requestConfig'])}
})
export default class App extends Mixins(Vue, SecurityMixin) {
    private logoPath: string | null = null;
    private footerLogoPath: string | null = null;
    private isNotifyPanelShow = false;
    private notificationModalInterval = 5 * 60 * 1000;
    messages: NotificationDto[] = [];
    themeConfig!: ThemeConfig;
    isThemeConfigLoading!: boolean;
    requestConfig!: () => Promise<ThemeConfig>;

    private moduleList: ModulePresetModel[] = [];

    get unreadMessagesCount(): number {
       return this.messages.filter((message) => !message.isRead).length || 0;
    }

    created() {
        setGlobalRir(this.$rir);
        // this.setTheme();
        setGlobalThemeType('default');
        this.notificationModalInterval = 5 * 60 * 1000;
        this.$store.commit(
            'setLinkSettings',
            `ru.rusatom.dev.digital.water.complex://upload/?server=https://${
                document.location.hostname
            }`
        );
        this.$store.commit(
            'setLinkDownloadApp',
            'http://files.digital-water.ru/complex.apk'
        );

        this.requestConfig().then( config => {
            window.document.title = config.documentTitle ?? 'Цифровой водоканал';
            this.getLogoPath(this.themeName, config);
        } )
        setRouterEvents();
        this.getFooterLogoPath(this.themeName, this.isMenuCompact);
        onMessageReceive.subscribe((message) => {
            this.messages.unshift(message);
            if ((message as any).eventCode === 'MAIN_EXPORT_XLSX_START') {
                progressToast(`Начат экспорт в XLSX-файл`);
            }
            if ((message as any).eventCode === 'MAIN_EXPORT_XLSX_END') {
                successToast(`Экспорт в XLSX-файл завершён`);
            }
        });
        this.$router.beforeEach(this.navigationGuard);
        window.addEventListener('beforeunload', this.onBeforeUnload);
    }

    setTheme() {
        const theme = localStorage.getItem('theme');
        if (theme) setGlobalThemeType(theme as ThemeType);
    }

    async navigationGuard(to: Route, from: Route, next: () => void) {
        if (localStorage.getItem('hasDirtyAttributes') !== 'true') {
            next();
        } else {
            const res = await this.$rir.modal.open(ConfirmModal, {}, {
                isCloseEventInitComponent: false
            });
            if (res === true) {
                localStorage.setItem('hasDirtyAttributes', 'false');
                next();
            } else {
                let url = from.path;
                if (from.query && Object.keys(from.query).length > 0) {
                    const query = [];
                    for (const key in from.query) {
                        query.push(`${key}=${from.query[key]}`);
                    }
                    url = `${url}?${query.join('&')}`;
                }
                window.history.replaceState(null, '', url);
            }
        }
    }

    onBeforeUnload() {
        if (this.$store.state.isForeign) {
            localStorage.removeItem('authData');
            clearPaginationSettings();
        }
    }

    @Watch('themeName')
    onThemeChange(themeName: string) {
        this.getLogoPath(themeName);
        this.getFooterLogoPath(themeName, this.isMenuCompact);
    }

    @Watch('isMenuCompact')
    onMenuChange(isMenuCompact: boolean) {
        this.getFooterLogoPath(this.themeName, isMenuCompact);
    }

    @Watch('$store.getters.isAuthenticated', { immediate: true })
    onAuthenticated(isAuthenticated: boolean) {
        if (isAuthenticated) {
            this.requestData();
        }
    }

    get rawLogo(): boolean {
        return this.themeConfig?.rawLogo ?? false;
    }

    get authData() {
        return this.$store.getters.authData;
    }

    get headerInfo(): IHeaderInfo {
        return this.$store.getters.getHeaderInfo;
    }

    get title(): string | null {
        return this.headerInfo?.title || null;
    }

    get subTitle(): string | null {
        return this.headerInfo?.subTitle || null;
    }

    get menu(): any[] {
        const menuList: IMenuEntity[] = this.$store.getters.menu;
        const newMenus: any[] = [];

        for (const menu of menuList) {
            newMenus.push( this.prepareMenu(menu) )
        }

        if ( this.lteWide ) {
            if ( this.authData ) {
                newMenus.push({
                    title: this.authData.name,
                    icon: 'avatar'
                });
                newMenus.push({
                    title: 'Выход',
                    icon: 'exit',
                    router: {
                        path: '/logout',
                        exact: true
                    }
                });
            }
        }

        return newMenus;
    }

    prepareMenu(menu: IMenuEntity | IMenuSubEntity): IItemsElement {
        const newMenu: IItemsElement = { title: menu.title, icon: menu.iconClass };
        if ( menu.path ) newMenu.router = { path: menu.path, exact: menu?.exact ?? true, query: {...menu?.query} };

        if ( menu.subMenus && menu.subMenus.length > 0 ) {
            newMenu.children = [];
            newMenu.router = undefined;
            for (const subMenu of menu.subMenus) {
                newMenu.children.push( this.prepareMenu(subMenu) );
            }
        }

        return newMenu;
    }

    toggleTheme() {
        toggleHtmlTheme();
    }

    getLogoPath(layoutTheme = 'default', themeConfig: ThemeConfig = this.themeConfig): void {
        if ( !themeConfig.themeName ) return;
        this.logoPath = `/themes/${themeConfig.themeName}/logo-${layoutTheme}.svg`;
    }

    getFooterLogoPath(layoutTheme = 'default', isMenuCompact = false): void {
        try {
            import(`@/assets/rir-logo-${layoutTheme}${isMenuCompact ? '-collapsed' : ''}.svg`).then((res) => {
                this.footerLogoPath = res.default;
            });
        } catch (error) {
            console.error("Ошибка загрузки файла логотипа");
        }
    }

    mounted() {
        setRirNotificationInistance(this.$rir.notification);
        const authData: IAuthData = this.$store.getters.authDataCached;
        if ( this.inAuthRoute ) {
            if ( !authData ) {
                localStorage.setItem('redirectTo', this.$route.fullPath);
                this.$router.push('/login');
            }
            else {
                this.$store.commit('setServicesLoadStart');
                getToken(authData).then( (response) => {
                    this.$store.commit('setAuthDataInit', response);
                    resubscribeToWebPush(response);
                    initMap();
                    initMicroFronts(response, this.$route.fullPath === '/');
                    connect(response);
                    subscribeToNotification(response);
                    subscribeToTokenUpdate(response);
                    bindUser(response);
                } );
            }
        }
        else {
            if ( authData ) {
                this.$router.push('/');
                this.$store.commit('setServicesLoadStart');
                getToken(authData).then( (response) => {
                    this.$store.commit('setAuthDataInit', response);
                    resubscribeToWebPush(response);
                    initMap();
                    initMicroFronts(response, true);
                    connect(response);
                    subscribeToNotification(response);
                    subscribeToTokenUpdate(response);
                    bindUser(response);
                } );
            }
        }
    }

    get inAuthRoute(): boolean {
        return !ignoreRoutes.some(ir => this.$route.fullPath.includes(ir));
    }

    get isMicroFrontsLoading(): boolean {
        const { isServicesLoading } = this.$store.state;

        return this.inAuthRoute && isServicesLoading;
    }

    get loadedServicesCount(): string {
        const processed = this.$store.state.processedServices;
        const total = this.$store.state.totalServices;
        return total > 1 ? `Модулей загружено: ${processed} из ${total}` : '';
    }

    async requestData(): Promise<void> {
        try {
            this.moduleList = await getModulePresets();
        } catch (error) {
            console.error(error);
            errorToast(`Ошибка получения списка шаблонов модулей`);
        }
        const url = `${Fetch.buildMessagingModuleUrl()}/notifications/search/currentUserMinimal`;
        Fetch.get<NotificationDto[]>(url)
            .then((notifyMessages) => {
                this.messages = notifyMessages || [];
            })
            .catch(error => {
                console.error(error);
                errorToast('Ошибка получения Оповещений');
            });
    }

    toggleNotifications() {
        this.isNotifyPanelShow = !this.isNotifyPanelShow;
    }
    closeNotifications() {
        this.isNotifyPanelShow = false;
    }

    onReadMessage(notificationId: string) {
        for (const message of this.messages) {
            if (message.notificationId === notificationId) {
                message.isRead = true;
            }
        }
    }
}
